import React, { useState } from "react";
import {
  Megaphone,
  Users,
  Handshake,
  Rocket,
  BadgeCheck,
  TrendingUp,
  Palette,
  Sparkle,
  ArrowUpRight,
  Plus,
  Minus,
} from "lucide-react";

const services = [
  {
    n: "01",
    icon: Megaphone,
    title: "PR, Media & Influencer Marketing",
    intro:
      "We position your brand with the right story, the right media angles, and the right visibility strategy to help you become recognized as an authority in your industry.",
    includes: [
      "Brand narrative and media angle development",
      "Digital press and editorial pitching",
      "Podcast, TV, online media and feature opportunities",
      "Influencer and creator collaboration strategy",
      "Media outreach coordination and follow-up",
      "Press-ready messaging, bios, talking points, and credibility assets",
    ],
    outcome:
      "A stronger public presence, elevated credibility, and media visibility that supports sales, partnerships, authority, and long-term brand recognition.",
    micro: "Build credibility through strategic media visibility.",
  },
  {
    n: "02",
    icon: Users,
    title: "Influencer Marketing",
    intro:
      "We design influencer marketing strategies that connect brands with the right creators, voices, and communities to generate trust, visibility, and measurable growth. Beyond posting content — aligned creators, authentic collaborations, and performance-driven activations.",
    includes: [
      "Influencer and creator strategy development",
      "Creator research, outreach, and relationship building",
      "Influencer campaign positioning and messaging",
      "Brand, creator, and community alignment",
      "Ambassador and affiliate program strategy",
      "Collaboration concepts and activation ideas",
      "Negotiation support and partnership coordination",
      "Launch collaborations and creator-led campaigns",
      "Influencer content direction and storytelling guidance",
      "Performance tracking recommendations and campaign optimization",
    ],
    outcome:
      "A strategic influencer campaign that builds authentic visibility, strengthens audience trust, and turns influence into awareness, engagement, qualified leads, and long-term growth.",
    micro: "Turn influence into trust, visibility, and measurable growth.",
  },
  {
    n: "03",
    icon: Handshake,
    title: "Strategic Partnerships",
    intro:
      "We help you identify, structure, and activate partnerships that expand your reach, create new opportunities, and connect your brand with aligned communities, platforms, founders, creators, and organizations.",
    includes: [
      "Partnership mapping and opportunity research",
      "Outreach strategy and positioning",
      "Collaboration and cross-promotion concepts",
      "Ambassador, referral, and affiliate partnership strategy",
      "Negotiation support and partner communication",
      "Launch and campaign partnership planning",
    ],
    outcome:
      "A stronger ecosystem around your brand — with strategic relationships that drive visibility, referrals, revenue opportunities, and long-term growth.",
    micro: "Turn relationships into strategic growth opportunities.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Digital Business & Product Launches",
    intro:
      "We support creators, experts, and brands in launching digital products, programs, events, courses, memberships, and offers with a clear message, audience strategy, and growth plan.",
    includes: [
      "Launch positioning and offer messaging",
      "Campaign strategy and timeline",
      "Landing page and sales copy direction",
      "Email, social, and content launch messaging",
      "Partnership and affiliate launch support",
      "Lead-generation and conversion strategy",
    ],
    outcome:
      "A more organized, strategic, and high-converting launch that connects your offer with the right audience and creates momentum before, during, and after launch.",
    micro: "Launch with clarity, strategy, and visibility.",
  },
  {
    n: "05",
    icon: BadgeCheck,
    title: "NYC Event Experiences & Brand Activations",
    intro:
      "We design elevated brand experiences that create connection, visibility, content opportunities, and memorable moments for your audience, partners, press, and community.",
    includes: [
      "Event concept and positioning",
      "Brand activation strategy",
      "Press and influencer invitation strategy",
      "Community and guest experience planning",
      "Content moments and storytelling ideas",
      "Partnership opportunities with venues, creators, and brands",
    ],
    outcome:
      "A curated event or activation that feels aligned with your brand, creates meaningful connection, and gives your audience a reason to remember, share, and engage.",
    micro: "Create experiences people remember — and talk about.",
  },
  {
    n: "06",
    icon: TrendingUp,
    title: "Growth Marketing · Lead Generation · AI Consulting",
    intro:
      "We help brands turn visibility into measurable opportunities through growth strategy, lead generation, funnels, automation, and AI-supported workflows.",
    includes: [
      "Growth audit and opportunity mapping",
      "Lead-generation strategy",
      "Funnel and customer journey recommendations",
      "AI workflow and automation consulting",
      "CRM, follow-up, and nurture sequence guidance",
      "Campaign optimization and reporting direction",
    ],
    outcome:
      "A smarter growth engine where visibility, content, leads, and follow-up systems work together to generate more qualified opportunities.",
    micro: "Turn visibility into leads, systems, and growth.",
  },
  {
    n: "07",
    icon: Palette,
    title: "Branding & Digital Marketing",
    intro:
      "We refine your brand message, online presence, and digital communication so your audience immediately understands who you are, what you offer, and why they should trust you.",
    includes: [
      "Brand positioning and messaging",
      "Website copy and content direction",
      "Social media content strategy",
      "Founder and executive positioning",
      "Visual brand direction and tone refinement",
      "Campaign storytelling and digital presence audit",
    ],
    outcome:
      "A clearer, more premium, and more credible brand presence that communicates authority across your website, social platforms, pitch materials, and sales conversations.",
    micro: "Make your brand impossible to ignore.",
  },
];

export default function Services() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-32 grain"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-16 lg:mb-20">
          <div className="col-span-12 md:col-span-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
                ✦ The services
              </span>
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
              Page 02
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2
              data-testid="services-headline"
              className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight font-light"
            >
              Premium PR, Branding &amp;
              <span className="italic text-[#7FB3D5]"> Digital Growth.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/70 max-w-3xl leading-relaxed">
              NOVA Media PR helps founders, creators, experts, entrepreneurs,
              and brands become more visible, credible, and commercially
              positioned through strategic media relations, influencer
              marketing, partnerships, launches, brand activations, and growth
              consulting.
            </p>
            <p className="mt-5 text-base md:text-lg text-white/55 max-w-3xl leading-relaxed italic font-display">
              We do not create visibility for the sake of attention. We build
              authority with intention — so your brand can be seen, heard,
              recognized, and trusted by the right audience.
            </p>
          </div>
        </div>

        {/* List */}
        <div className="border-t border-white/10">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const isOpen = openIdx === idx;
            return (
              <div
                key={s.n}
                data-testid={`service-row-${s.n}`}
                className={`group border-b border-white/10 transition-colors duration-500 ${
                  isOpen ? "bg-white/[0.02]" : "hover:bg-white/[0.015]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  data-testid={`service-toggle-${s.n}`}
                  className="w-full text-left grid grid-cols-12 gap-4 md:gap-6 py-8 md:py-12 relative"
                  aria-expanded={isOpen}
                >
                  {/* Number */}
                  <div
                    className={`col-span-2 md:col-span-1 font-display text-3xl md:text-5xl transition-colors duration-500 ${
                      isOpen
                        ? "text-[#7FB3D5]"
                        : "text-white/30 group-hover:text-[#7FB3D5]"
                    }`}
                  >
                    {s.n}
                  </div>

                  {/* Icon */}
                  <div className="col-span-2 md:col-span-1 flex items-start pt-1 md:pt-2">
                    <div
                      className={`w-11 h-11 md:w-14 md:h-14 rounded-full border flex items-center justify-center transition-all duration-500 ${
                        isOpen
                          ? "bg-[#7FB3D5] text-black border-[#7FB3D5]"
                          : "border-[#7FB3D5]/30 text-[#7FB3D5] group-hover:bg-[#7FB3D5] group-hover:text-black"
                      }`}
                    >
                      <Icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.2} />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="col-span-8 md:col-span-9">
                    <h3 className="font-display text-2xl md:text-4xl text-white font-light leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-[#7FB3D5]/80">
                      ✦ {s.micro}
                    </p>
                  </div>

                  {/* Toggle icon */}
                  <div className="hidden md:flex col-span-1 justify-end items-start pt-3">
                    <span
                      className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-500 ${
                        isOpen
                          ? "border-[#7FB3D5] bg-[#7FB3D5] text-black"
                          : "border-white/20 text-white/60 group-hover:border-[#7FB3D5] group-hover:text-[#7FB3D5]"
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="w-4 h-4" strokeWidth={1.5} />
                      ) : (
                        <Plus className="w-4 h-4" strokeWidth={1.5} />
                      )}
                    </span>
                  </div>
                </button>

                {/* Expandable detail */}
                <div
                  className={`grid grid-cols-12 gap-6 overflow-hidden transition-all duration-700 ease-out ${
                    isOpen
                      ? "max-h-[2400px] opacity-100 pb-12 md:pb-16"
                      : "max-h-0 opacity-0"
                  }`}
                  data-testid={`service-detail-${s.n}`}
                >
                  <div className="hidden md:block col-span-2" />
                  <div className="col-span-12 md:col-span-6 md:pr-8">
                    <p className="text-base md:text-lg text-white/75 leading-relaxed">
                      {s.intro}
                    </p>
                    <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] mb-4">
                      What this service includes
                    </p>
                    <ul className="space-y-3">
                      {s.includes.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-3 text-sm md:text-base text-white/65 leading-relaxed"
                        >
                          <Sparkle
                            className="w-2.5 h-2.5 text-[#7FB3D5] mt-2 shrink-0"
                            fill="#7FB3D5"
                            strokeWidth={0.6}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="col-span-12 md:col-span-4 md:pl-4">
                    <div className="border-l border-[#7FB3D5]/30 pl-6 sticky top-32">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] mb-3">
                        Ideal outcome
                      </p>
                      <p className="font-display text-xl md:text-2xl text-white font-light italic leading-snug">
                        {s.outcome}
                      </p>
                      <a
                        href="#contact"
                        data-testid={`service-cta-${s.n}`}
                        className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white hover:text-[#7FB3D5] transition-colors duration-300 border-b border-white/20 hover:border-[#7FB3D5] pb-1"
                      >
                        Book a 30-min consultation
                        <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.2} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Closing copy + Investment positioning */}
        <div className="grid grid-cols-12 gap-6 mt-20 md:mt-28">
          <div className="col-span-12 md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5] mb-6">
              ✦ From visibility to opportunity
            </p>
            <p className="font-display text-2xl md:text-4xl text-white font-light leading-snug">
              Every service is designed to help your brand move from{" "}
              <span className="italic text-[#7FB3D5]">visibility</span> to
              authority — and from authority to{" "}
              <span className="italic text-[#7FB3D5]">opportunity</span>.
            </p>
            <p className="mt-6 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl">
              Whether you are launching a new offer, entering a new market,
              building media credibility, or activating strategic partnerships,
              NOVA Media PR creates the positioning, outreach, and growth
              strategy to support your next level.
            </p>
            <a
              href="#contact"
              data-testid="services-closing-cta"
              className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-[#7FB3D5] text-black text-[11px] uppercase tracking-[0.3em] hover:bg-white transition-colors duration-500"
            >
              Book a 30-minute consultation
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.2} />
            </a>
          </div>

          <div className="col-span-12 md:col-span-5 md:pl-10">
            <div className="border border-[#7FB3D5]/25 p-8 md:p-10 bg-[#080808] relative">
              <Sparkle
                className="absolute -top-3 -left-3 w-6 h-6 text-[#7FB3D5] bg-[#050505] p-0.5"
                fill="#7FB3D5"
                strokeWidth={0.6}
              />
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5] mb-5">
                Investment
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white font-light leading-snug">
                Customized to your goals,
                <span className="italic text-[#7FB3D5]"> timeline</span> &amp;
                market.
              </h3>
              <p className="mt-5 text-sm md:text-base text-white/65 leading-relaxed">
                Our services are tailored to your goals, timeline, market, and
                level of execution required. PR and media projects typically
                begin at premium project-based investment levels, depending on
                the scope, number of outlets, campaign duration, and
                deliverables included.
              </p>
              <p className="mt-4 text-sm md:text-base text-white/55 leading-relaxed italic font-display">
                During the consultation, we&apos;ll review your goals and
                recommend the most strategic path forward.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
